import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem
} from '@angular/cdk/drag-drop';
import * as Workflow from 'esri/widgets/Editor/Workflow';
import {
  ConditionItem,
  ConditionSource,
  ConditionTarget
} from 'src/app/components/system/condition-builder/condition-builder.model';
import {
  Activity,
  ActivityModel,
  DataEntityLayoutModel,
  DataEntityRowLayoutModel
} from 'src/app/models/activities';
import { DataEntityEditorComponent } from '../../data-entity-editor/data-entity-editor.component';
import {
  LAYOUT_DROP_LISTS,
  canEnterEntityLayout,
  registerLayoutDropList,
  unregisterLayoutDropList
} from '../../layout-drop-lists';
import { WorkflowContextService } from 'src/app/services/workflow-context.service';
import { WorkflowService } from 'src/app/services';

@Component({
  selector: 'wm-data-entity-row-layout-editor',
  templateUrl: './data-entity-row-layout-editor.component.html',
  styleUrl: './data-entity-row-layout-editor.component.css',
  standalone: false
})
export class DataEntityRowLayoutEditorComponent implements OnInit, OnDestroy {
  @Input() layout: DataEntityRowLayoutModel;
  @Input() workflow: Workflow;
  @Input() activity: Activity<ActivityModel>;
  @Input() form: UntypedFormGroup;
  @ViewChild('newDEEditor', { static: false })
  newDEEditor: DataEntityEditorComponent;

  newDEForm: UntypedFormGroup;
  showEditor: boolean;

  showConditionalVisible = false;
  conditionSources: ConditionSource[];

  get rowEntities() {
    return this.layout?.rowEntities || [];
  }

  saveNew() {
    this.layout.rowEntities.push(
      new DataEntityLayoutModel({ entity: this.newDEEditor.dataEntity })
    );
  }
  cancelNew() {
    this.showEditor = false;
  }
  loadEditor() {
    this.showEditor = true;
    this._ref.detectChanges();
  }

  visibleCriteriaChanged(e) {
    this.layout.visibleCriteria.group = e;
  }

  visibleConditionChanged() {
    // Handle checkbox state changes
    if (this.showConditionalVisible) {
      // Initialize condition criteria when enabling
      if (this.layout.visibleCriteria == null) {
        this.layout.visibleCriteria = new ConditionTarget({
          group: new ConditionItem({ isGroup: true }),
          value: 'Yes'
        });
      }
    } else {
      // Clear criteria when disabling (optional - you may want to preserve the criteria)
    }

    // Trigger change detection to ensure UI updates properly
    this._ref.detectChanges();
  }

  constructor(
    private _workflowSvc: WorkflowService,
    private _context: WorkflowContextService,
    private _ref: ChangeDetectorRef
  ) {}

  removeDataEntity(de, index: number) {
    this.layout.rowEntities.splice(index, 1);
  }

  viewDataentity(de) {}

  saveDataEntity(de) {
    // Update the data entity in the layout
  }

  onSaved(e) {}

  drop(event: CdkDragDrop<DataEntityLayoutModel[]>) {
    if (event.previousContainer === event.container) {
      // Same container - just reorder
      if (this.layout?.rowEntities) {
        moveItemInArray(
          this.layout.rowEntities,
          event.previousIndex,
          event.currentIndex
        );

        // Update display orders
        this.layout.rowEntities.forEach((entity, index) => {
          if (entity) {
            entity.displayOrder = index + 1;
          }
        });

        this._ref.detectChanges();
      }
    } else {
      // Different container - transfer item

      if (this.layout?.rowEntities && event.previousContainer.data) {
        transferArrayItem(
          event.previousContainer.data,
          this.layout.rowEntities,
          event.previousIndex,
          event.currentIndex
        );

        // Update display orders for both arrays
        this.layout.rowEntities.forEach((entity, index) => {
          if (entity) {
            entity.displayOrder = index + 1;
          }
        });

        // Update display orders for source array
        event.previousContainer.data.forEach((entity, index) => {
          if (entity) {
            entity.displayOrder = index + 1;
          }
        });

        this._ref.detectChanges();
      }
    }
  }

  // unique per instance: a form can contain many Rows / Groups
  readonly dropListId = registerLayoutDropList('row-entities-list');
  readonly connectedDropLists = LAYOUT_DROP_LISTS;
  readonly canEnter = canEnterEntityLayout;

  ngOnDestroy(): void {
    unregisterLayoutDropList(this.dropListId);
  }

  ngOnInit(): void {
    this.newDEForm = new UntypedFormGroup({});

    // Initialize conditional visibility toggle based on existing criteria
    this.initializeConditionalVisibility();

    this._workflowSvc
      .getDataEntitiesBeforeMe(this._context.workflow, this.activity)
      .subscribe(entities => {
        this.conditionSources = entities.map(e => {
          return {
            name: e.templateCode,
            value: e.templateCode,
            operators: e.getAvailableOperators(),
            possibleValues: e.getPossibleValues()
          };
        });
      });
  }

  private initializeConditionalVisibility(): void {
    // Check if there are existing conditional criteria defined
    this.showConditionalVisible = this.hasConditionalCriteria();
  }

  private hasConditionalCriteria(): boolean {
    return !!(
      this.layout?.visibleCriteria &&
      this.layout.visibleCriteria.group &&
      this.layout.visibleCriteria.group.conditions?.length > 0
    );
  }

  /**
   * Call this method to refresh the conditional visibility state
   * based on current criteria. Useful when layout is updated externally.
   */
  public refreshConditionalVisibility(): void {
    this.showConditionalVisible = this.hasConditionalCriteria();
    this._ref.detectChanges();
  }
}
